/* eslint-disable no-restricted-imports */
import React, {useEffect, useMemo, useRef} from 'react'
import {UIGameActionButton} from '../../../../common/UIGameActionButton'
import {T, Tooltip} from '@colonist/ui'
import {CardEnum, CityImprovementTypes} from '@colonist/game-states'
import {
    UIGameCityImprovementTrackerSquare,
} from '../../../../common/UIGameCityImprovementTracker/UIGameCityImprovementTrackerSquare/UIGameCityImprovementTrackerSquare'
import {
    UIGameResourceCardsTooltipContent,
    UIGameResourceCardsTooltipContentHandle,
} from '../../../../common/UIGameResourceCardsTooltipContent'
import {useActionButtonCommonErrors} from '../../utils'
import {TranslationObject} from '@colonist/i18n'
import {EnumUtils} from '@colonist/utils'
import {useGameStore} from '../../../../store/GameStoreContext'
import {usePlayerValidators} from '../../../../store/selectors/usePlayerValidators'
import {ActionStatus} from '@colonist/game-base-types'
import {useUIGameSocketContext} from '../../../../contexts/UIGameSocketContext'
import styles from '../UIGameActionButtonCityImprovements.module.scss'
import {useMechanicValidatorCityImprovements} from '../../../../store/selectors/useMechanicValidatorCityImprovements'

export interface UIGameActionButtonCityImprovementsItemProps {
    className?: string
    cityImprovementType: CityImprovementTypes
    hideTooltip?: boolean | undefined
    onDisabledChange: (isDisabled: boolean) => void
}

export const UIGameActionButtonCityImprovementsItem = ({
    cityImprovementType,
    className,
    hideTooltip,
    onDisabledChange,
}: UIGameActionButtonCityImprovementsItemProps) => {
    const myColor = useGameStore((state) => state.myColor)
    const {getPlayerValidator} = usePlayerValidators()
    const playerValidator = getPlayerValidator(myColor)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const tooltipRef = useRef<UIGameResourceCardsTooltipContentHandle>(null)
    const {sendConfirmCityUpgrade} = useUIGameSocketContext()
    const cityImprovementValidator = useMechanicValidatorCityImprovements()
    const {actionStatus, costToUpgrade}: {
        actionStatus: ActionStatus
        costToUpgrade?: CardEnum[] | undefined
    } = useMemo(() => {
        if(!cityImprovementValidator) {
            return {actionStatus: {canTakeAction: false}}
        }

        const costToUpgradeLocal = cityImprovementValidator.getCostForNextCityUpgradeForPlayerAndType(myColor, cityImprovementType)
        const canUpgradeCityActionStatus = cityImprovementValidator.canPlayerUpgradeCity(myColor, cityImprovementType)

        return {
            actionStatus: canUpgradeCityActionStatus,
            costToUpgrade: costToUpgradeLocal,
        }
    }, [cityImprovementValidator, myColor, cityImprovementType])

    const triggerFlickerCardsAnimation = () => {
        const notEnoughResources = !isAtMaxLevel ? !playerValidator.hasResourceCardsInHand(costToUpgrade) : false
        if(notEnoughResources) {
            tooltipRef.current?.triggerFlickerAnimation()
        }
    }

    const isAtMaxLevel = costToUpgrade == undefined
    const disabled = hasCommonErrors || !actionStatus.canTakeAction
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction
    const improvementTypeKey = EnumUtils.getEnumKey(cityImprovementType, CityImprovementTypes)
    const translationText: TranslationObject = {key: `strings:game.cards.progress.title.${improvementTypeKey}`}

    useEffect(() => {
        onDisabledChange(disabled)
    }, [disabled])

    const onClick = () => {
        if(disabled) return
        sendConfirmCityUpgrade(cityImprovementType)
    }

    return <Tooltip
        position='bottom'
        disabled={hideTooltip}
        title={<T id={translationText}/>}
    >
        <UIGameActionButton
            className={className}
            countColor={myColor}
            disabled={disabled}
            helperText={helperText}
            iconClassName={styles.cityImprovementTrackerSquare}
            customIconComponent={
                <UIGameCityImprovementTrackerSquare
                    cityImprovementType={cityImprovementType}
                    active={!disabled} />
            }
            onClickActionIfDisabled={triggerFlickerCardsAnimation}
            tooltipCustomMenuComponent={
                !isAtMaxLevel && !hideTooltip
                && <UIGameResourceCardsTooltipContent
                    cardInventory={playerValidator.resourceCards.cards}
                    cardsToBuild={costToUpgrade}
                    ref={tooltipRef}/>
            }
            onClick={onClick}
        />
    </Tooltip>
}
